/**
 * C0-LOOK P1: the daytime grade. By day the world was raw material colours; this pass adds, over the
 * finished world picture and under the labels, (all drawn once into cached canvases at the art
 * grid, Bayer 4x4 dithered so it stays pixel, then composited each frame):
 *   - a flat warm gold wash (source-over) and cool violet in the shade (multiply),
 *   - drifting cloud shadows that lie on the ground (the pattern moves with the camera, 3 steps),
 *   - light shafts from the upper right (2 steps, slowly breathing),
 *   - a 4-step cool vignette and a low mist at the foot of the frame.
 * Strength follows the hour: full by day, gone at night (the dusk/night grade of render/lighting.ts
 * takes over, unchanged). Setting `dayGrade`. Cost: a few full-picture drawImage composites.
 */
import '../dayGrade.setting';
import { settings } from '../../settings';
import { lightingOn, nightAt } from '../lighting';
import { registerRenderPass } from './registry';

const BAY = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map((v) => (v + 0.5) / 16);
const bay = (x: number, y: number) => BAY[(y & 3) * 4 + (x & 3)]!;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

function hash(x: number, y: number, s: number): number {
  let h = (x * 374761393 + y * 668265263 + s * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
/** Value noise on a lattice that wraps every `px` x `py` cells (so a tile repeats seamlessly). */
function wrapNoise(x: number, y: number, px: number, py: number, s: number): number {
  const xi = Math.floor(x), yi = Math.floor(y);
  const fx = x - xi, fy = y - yi;
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  const h = (a: number, b: number) => hash(((a % px) + px) % px, ((b % py) + py) % py, s);
  const a = h(xi, yi), b = h(xi + 1, yi), c = h(xi, yi + 1), d = h(xi + 1, yi + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}

function layer(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return [c, c.getContext('2d', { willReadFrequently: true })!];
}

const TILE = 256;
/** The cloud-shadow tile: multiply factors in 3 dithered steps, cells elongated along the iso x axis. */
function cloudTile(): HTMLCanvasElement {
  const [c, g] = layer(TILE, TILE);
  const id = g.createImageData(TILE, TILE);
  const d = id.data;
  for (let y = 0; y < TILE; y++)
    for (let x = 0; x < TILE; x++) {
      const n =
        0.62 * wrapNoise(x / 64, y / 32, TILE / 64, TILE / 32, 11) +
        0.38 * wrapNoise(x / 28, y / 14, TILE / 28 + 0.0001, TILE / 14 + 0.0001, 12);
      const v = clamp((n - 0.52) / 0.14, 0, 1) * 2;
      const step = Math.min(2, Math.floor(v) + (v % 1 > bay(x, y) ? 1 : 0));
      const m = [1, 0.9, 0.8][step]!;
      const i = (y * TILE + x) * 4;
      d[i] = Math.round(255 * m * 0.97);
      d[i + 1] = Math.round(255 * m * 0.99);
      d[i + 2] = Math.round(Math.min(255, 255 * m * 1.04));
      d[i + 3] = 255;
    }
  g.putImageData(id, 0, 0);
  return c;
}

interface Cache {
  w: number;
  h: number;
  /** Multiply layer: the vignette. */
  vig: HTMLCanvasElement;
  /** Light layer (source-over, low alpha): the shafts and the mist. */
  lit: HTMLCanvasElement;
  cloud: HTMLCanvasElement;
}

function build(w: number, h: number, cloud: HTMLCanvasElement): Cache {
  const [vig, vg] = layer(w, h);
  const vi = vg.createImageData(w, h);
  const [lit, lg] = layer(w, h);
  const li = lg.createImageData(w, h);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      // Vignette: 4 dithered steps, cool.
      const vx = (x - w / 2) / (w / 2), vy = (y - h / 2) / (h / 2);
      const vv = clamp((vx * vx + vy * vy - 0.5) / 1.0, 0, 1) * 4;
      const vs = Math.floor(vv) + (vv % 1 > bay(x, y) ? 1 : 0);
      const m = 1 - 0.1 * vs;
      vi.data[i] = Math.round(255 * Math.min(1, m * (vs ? 0.96 : 1)));
      vi.data[i + 1] = Math.round(255 * Math.min(1, m * (vs ? 0.98 : 1)));
      vi.data[i + 2] = 255;
      vi.data[i + 3] = 255;
      // Shafts from the upper right (bands along the iso diagonal), 2 steps.
      const s = x * 1.2 + y * 1.0;
      const band = Math.max(0, Math.sin(s / 31) * Math.sin(s / 83 + 1.3));
      const fade = clamp(0.9 * (x / w) + 0.7 * (1 - y / h) - 0.45, 0, 1);
      if (band * band * fade * 2.2 > bay(x, y) * 0.9) {
        li.data[i] = 255;
        li.data[i + 1] = 236;
        li.data[i + 2] = 170;
        li.data[i + 3] = 34;
      }
      // Low mist at the foot of the frame.
      const mt = clamp((y - h * 0.84) / (h * 0.16), 0, 1) * 0.9;
      if (mt > bay(x, y)) {
        li.data[i] = 205;
        li.data[i + 1] = 196;
        li.data[i + 2] = 222;
        li.data[i + 3] = 34;
      }
    }
  vg.putImageData(vi, 0, 0);
  lg.putImageData(li, 0, 0);
  return { w, h, vig, lit, cloud };
}

/** Strength per level (index = setting value; 0 is off). soft: warm wash alpha (source-over); cool: violet multiply alpha; cloud, vig, lit: scale of those layers. */
export const LEVELS = [
  null,
  { soft: 0.12, cool: 0.08, cloud: 0.5, vig: 0.35, lit: 0.5 },
  { soft: 0.22, cool: 0.14, cloud: 0.75, vig: 0.55, lit: 0.8 },
] as const;

/** The day grade's share at an hour (nightAt's dark, warm): full by day, giving way to the dusk and night grades. */
export const dayShare = (dark: number, warm: number): number => clamp(1 - dark * 1.6 - warm * 0.55, 0, 1);

/** Built layers per picture size (the world's, and the front stage's while both draw, e.g. under the loading scene). */
const caches: Cache[] = [];
let tile: HTMLCanvasElement | null = null;
const pats = new WeakMap<CanvasRenderingContext2D, CanvasPattern>();

/**
 * Paints the day grade over a finished picture `W` x `H` (in the context's units) at `day` share (dayShare), level
 * `lv`; `time` drifts the clouds and breathes the shafts, `ox`/`oy` anchor the clouds to the ground. Shared with the
 * front stage (front/hourTone.ts), so the title picture gets the same grade as the world.
 */
export function paintDayGrade(
  g: CanvasRenderingContext2D,
  W: number,
  H: number,
  lv: NonNullable<(typeof LEVELS)[number]>,
  day: number,
  time: number,
  ox: number,
  oy: number,
): void {
  let cache = caches.find((c) => c.w === W && c.h === H);
  if (!cache) {
    tile ??= cloudTile();
    cache = build(W, H, tile);
    caches.unshift(cache);
    caches.length = Math.min(caches.length, 2);
  }
  g.save();
  g.globalAlpha = day;
  // Warm wash, source-over: the GPU canvas (render/webgl.ts blend) has no soft-light and always drew this as a flat
  // wash; that is the liked day look, so it is asked for as such (render/passes/hourWash.ts carries it into dusk/night).
  g.globalCompositeOperation = 'source-over';
  g.fillStyle = `rgba(255,214,140,${lv.soft})`;
  g.fillRect(0, 0, W, H);
  // Cool shade: a violet multiply that bites the dark tones most.
  g.globalCompositeOperation = 'multiply';
  g.globalAlpha = day * lv.cool;
  g.fillStyle = 'rgb(186,192,238)';
  g.fillRect(0, 0, W, H);
  g.globalAlpha = day;
  // Cloud shadows lie on the ground: the pattern is anchored to the world and drifts slowly.
  let pat = pats.get(g);
  if (!pat) {
    pat = g.createPattern(cache.cloud, 'repeat') ?? undefined;
    if (pat) pats.set(g, pat);
  }
  if (pat) {
    const dx = Math.round(ox + time * 1.4);
    const dy = Math.round(oy + time * 0.7);
    pat.setTransform(new DOMMatrix().translate(dx, dy));
    g.globalAlpha = day * lv.cloud;
    g.fillStyle = pat;
    g.fillRect(0, 0, W, H);
    g.globalAlpha = day;
  }
  g.globalAlpha = day * lv.vig;
  g.drawImage(cache.vig, 0, 0);
  g.globalAlpha = day;
  // Shafts (breathing a little) and the mist.
  g.globalCompositeOperation = 'source-over';
  g.globalAlpha = day * lv.lit * (0.8 + 0.2 * Math.sin(time * 0.35));
  g.drawImage(cache.lit, 0, 0);
  g.restore();
}

registerRenderPass({
  id: 'dayGrade',
  layer: 'world',
  order: 410,
  draw(f) {
    const lv = LEVELS[settings.dayGradeLevel];
    if (!f.outdoors || f.scene || !lightingOn() || !lv) return;
    const { dark, warm } = nightAt(f.world.hour);
    const day = dayShare(dark, warm);
    if (day < 0.02) return;
    paintDayGrade(f.g, f.W, f.H, lv, day, f.world.time, f.ox, f.oy);
  },
});
